/**
 * How the Tablero should degrade when `GET /rounds/{id}/report` fails.
 *
 * The report only exists once a round has a `summary_snapshot`, and that is
 * written by exactly one path: the end of a batch run (`batch.py`). A round fed
 * by the widget is processed one expediente at a time and never runs a batch,
 * so its report 404s FOREVER — "aún no disponible" is not a transient state
 * there (issue #217).
 *
 * The expediente list, however, comes from a different endpoint
 * (`/rounds/{id}/expedientes`) that answers fine in that situation. So a
 * missing report must not hide the expedientes; only a genuinely missing round
 * should.
 *
 * Kept as a pure function (no React, no DOM) so it is unit-testable under the
 * SPA's Node-only vitest setup.
 */

import { isApiError } from "../api/client";

export type ReportGap =
  /** The report loaded (or is still loading): render the full dashboard. */
  | "NONE"
  /** No snapshot yet/ever — show the notice AND the expediente browser. */
  | "NO_REPORT"
  /** The round itself does not exist for this tenant — nothing to list. */
  | "NO_ROUND"
  /** Anything else (5xx, corrupt snapshot, network): a real error. */
  | "ERROR";

/**
 * Classify a `/report` failure.
 *
 * Only a 404 is a "gap"; the two 404 codes mean very different things:
 *   - `ROUND_NOT_FOUND`   → the round doesn't exist (or isn't ours) → NO_ROUND
 *   - `REPORT_UNAVAILABLE`→ the round exists, the snapshot doesn't  → NO_REPORT
 *
 * A 404 with an unexpected/absent code is treated as NO_REPORT: the round was
 * reachable enough to be asked about, and the browser below fails safe on its
 * own (it renders its own error state if the list 404s too).
 *
 * A 502 `REPORT_UNAVAILABLE` (corrupt snapshot) is NOT a 404 and stays a real
 * error, as before.
 */
export function classifyReportGap(error: unknown): ReportGap {
  if (!error) return "NONE";
  if (!isApiError(error) || error.status !== 404) return "ERROR";
  return error.code === "ROUND_NOT_FOUND" ? "NO_ROUND" : "NO_REPORT";
}
